import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { StoreAuxService } from '../../services/storeAux/store-aux.service';
import { environment } from '../../../environments/environment';

import {
  CatalogListResponse,
  Company,
  Contact,
  ExternalIdAvailability,
  FileUploadData,
  FinancesConfig,
  OwnersSearchResponse,
  PurchaseOrder,
  PurchaseOrderCreateRequest,
  PurchaseOrderDetail,
  PurchaseOrderSearchFilters,
  PurchaseOrderSearchResponse,
  PurchaseOrderUpdateRequest
} from '../models/makers-finances-mgr.models';

@Injectable()
export class MakersFinancesMgrService {
  private readonly BASE_PATH = `${environment.urlGatewayInt}/makers-finances-mgr`;

  readonly config$ = new BehaviorSubject<FinancesConfig | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly storeAux: StoreAuxService
  ) {}

  resolveCountry(country: string): string {
    return country === 'DEV' ? 'CO' : country;
  }

  getConfigs(country: string): Observable<{ message: string; data: FinancesConfig }> {
    const params = new HttpParams({ fromObject: { country: this.resolveCountry(country).toLowerCase() } });
    return this.http.get<{ message: string; data: FinancesConfig }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/configs`),
      { params }
    ).pipe(tap(res => this.config$.next(res.data)));
  }

  searchCompanies(
    country: string,
    q?: string,
    options: { agencyOnly?: boolean } = {}
  ): Observable<{ message: string; data: Company[] }> {
    let fromObject: Record<string, string> = { country: this.resolveCountry(country) };
    if (q) {
      fromObject = { ...fromObject, q };
    }
    if (options.agencyOnly !== undefined) {
      fromObject = { ...fromObject, agency_only: String(options.agencyOnly) };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/search`),
      { params, responseType: 'text' }
    ).pipe(map(text => this._parseSafeJson<{ message: string; data: Company[] }>(text)));
  }

  searchContacts(companyId: string, q?: string): Observable<{ message: string; data: Contact[] }> {
    let fromObject: Record<string, string> = { company_id: companyId };
    if (q) {
      fromObject = { ...fromObject, q };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/contacts/search`),
      { params, responseType: 'text' }
    ).pipe(map(text => this._parseSafeJson<{ message: string; data: Contact[] }>(text)));
  }

  previewFile(key: string, country: string): Observable<Blob> {
    const params = new HttpParams({ fromObject: { key, country } });
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/files/preview`),
      { params, responseType: 'blob' }
    );
  }

  uploadFile(file: File, country: string): Observable<{ message: string; data: FileUploadData }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('country', this.resolveCountry(country));
    return this.http.post<{ message: string; data: FileUploadData }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/files/upload`),
      formData
    );
  }

  searchOwners(country: string, page = 1, limit = 10, q?: string): Observable<OwnersSearchResponse> {
    let fromObject: Record<string, string> = { country: this.resolveCountry(country), page: String(page), limit: String(limit) };
    if (q) {
      fromObject = { ...fromObject, q };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get<OwnersSearchResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/owners/search`),
      { params, headers: this._poHeaders() }
    );
  }

  // HU3 — POST /purchase-orders/search?page=&limit=
  // page/limit travel in the query string (the backend ignores them in the body via json:"-").
  // Filters travel in the body; only present fields are sent.
  searchPurchaseOrders(
    filters: PurchaseOrderSearchFilters,
    page = 1,
    limit = 10,
  ): Observable<{ message: string; data: PurchaseOrderSearchResponse }> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    const body = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== null),
    );
    return this.http.post<{ message: string; data: PurchaseOrderSearchResponse }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/search`),
      body,
      { params, headers: this._poHeaders() },
    );
  }

  // HU3 — GET /purchase-orders/:id
  // Parsed as text + _parseSafeJson so the 19-digit Zoho company_to_bill_id is preserved as a
  // string instead of being truncated by JSON.parse (same pattern as searchCompanies).
  getPurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrderDetail }> {
    return this.http.get(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}`),
      { headers: this._poHeaders(), responseType: 'text' },
    ).pipe(map(text => this._parseSafeJson<{ message: string; data: PurchaseOrderDetail }>(text)));
  }

  // PR #38: on-blur validation against GET /purchase-orders/external-id/availability.
  // companyToBillId is the Zoho int64 held as a string (see _stringifyBody) to avoid float
  // precision loss. excludeId is optional (only used when editing an existing PO).
  checkExternalIdAvailability(
    externalId: string,
    companyToBillId: string,
    country: string,
    excludeId?: number,
  ): Observable<{ message: string; data: ExternalIdAvailability }> {
    let fromObject: Record<string, string> = {
      external_id: externalId,
      company_to_bill_id: companyToBillId,
      country: this.resolveCountry(country),
    };
    if (excludeId != null) {
      fromObject = { ...fromObject, exclude_id: String(excludeId) };
    }
    const params = new HttpParams({ fromObject });
    return this.http.get<{ message: string; data: ExternalIdAvailability }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/external-id/availability`),
      { params, headers: this._emailHeaders() },
    );
  }

  createPurchaseOrder(body: PurchaseOrderCreateRequest): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders`),
      this._stringifyBody(body),
      { headers: this._poHeaders().set('Content-Type', 'application/json') }
    );
  }

  updatePurchaseOrder(id: number, body: PurchaseOrderUpdateRequest): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.put<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}`),
      this._stringifyBody(body),
      { headers: this._poHeaders().set('Content-Type', 'application/json') }
    );
  }

  submitPurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/submit`),
      null,
      { headers: this._poHeaders() }
    );
  }

  // POST /purchase-orders/:id/cancel — transitions a pre-approval PO to `canceled`.
  // `reason` is optional (reserved for audit). Returns the full PO representation.
  cancelPurchaseOrder(id: number, reason?: string): Observable<{ message: string; data: PurchaseOrder }> {
    const body = reason != null && reason !== '' ? { reason } : null;
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/cancel`),
      body,
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/close — transitions an approved PO to `closed`.
  closePurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/close`),
      {},
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/reopen — transitions a closed PO back to `approved`.
  reopenPurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/reopen`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/approve — approves a PO (client or paid_by_rappi).
  approvePurchaseOrder(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/approve`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/return-to-kam — returns a PO to the KAM with a required comment.
  returnToKam(id: number, comment: string): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/return-to-kam`),
      { comment },
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/resubmit — revalidates and resubmits a PO returned to KAM.
  // `comment` is optional (max 2000 chars).
  resubmitPurchaseOrder(id: number, comment?: string): Observable<{ message: string; data: PurchaseOrder }> {
    const body = comment != null && comment !== '' ? { comment } : null;
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/resubmit`),
      body,
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /purchase-orders/:id/retry-settlement — retries settlement registration.
  // Note: response has no `data` field, only `message`.
  retrySettlement(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/retry-settlement`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/retry-trato — retries Trato contract creation.
  retryTrato(id: number): Observable<{ message: string; data: PurchaseOrder }> {
    return this.http.post<{ message: string; data: PurchaseOrder }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/retry-trato`),
      null,
      { headers: this._poHeaders() },
    );
  }

  // POST /purchase-orders/:id/comments — adds a comment to a PO (no stage transition).
  // Response has no `data`, only `message`.
  addComment(id: number, body: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/purchase-orders/${id}/comments`),
      { body },
      { headers: this._poHeaders().set('Content-Type', 'application/json') },
    );
  }

  // POST /mdm-core/{country}/makers/search — MDM maker search for filter dropdowns.
  searchMakers(page: number, size: number, search: string): Observable<{ content: { id: number; code: string; name: string }[] }> {
    const url = `${this.storeAux.getCountry(environment.urlMdmCore)}/makers/search`;
    const params = new HttpParams({ fromObject: {
      page: String(page),
      size: String(size),
      direction: 'DESC',
      orderBy: 'createdAt',
    }});
    return this.http.post<{ content: { id: number; code: string; name: string }[] }>(
      url,
      search ? { name: search } : {},
      { params },
    );
  }

  getSourceTypes(page = 1, limit = 50): Observable<CatalogListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    return this.http.get<CatalogListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/source-types`),
      { params, headers: this._emailHeaders() }
    );
  }

  getTagOptions(page = 1, limit = 50): Observable<CatalogListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    return this.http.get<CatalogListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/tag-options`),
      { params, headers: this._emailHeaders() }
    );
  }

  getPoTypeOptions(page = 1, limit = 50): Observable<CatalogListResponse> {
    const params = new HttpParams({ fromObject: { page: String(page), limit: String(limit) } });
    return this.http.get<CatalogListResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/po-type-options`),
      { params, headers: this._emailHeaders() }
    );
  }

  private _emailHeaders(): HttpHeaders {
    return new HttpHeaders({ email: localStorage.getItem('email') || '' });
  }

  // PR #56: all Purchase Order endpoints require a `country` header (ISO alpha-2).
  // resolveCountry maps DEV → CO.
  private _poHeaders(): HttpHeaders {
    return this._emailHeaders().set('country', this.resolveCountry(this.storeAux.country));
  }

  // Quotes integers with 16+ digits before JSON.parse to prevent float precision loss.
  private _parseSafeJson<T>(text: string): T {
    return JSON.parse(text.replace(/(:\s*)(\d{16,})/g, '$1"$2"'));
  }

  // Zoho IDs (company_to_bill_id, contact_id, agency_id) are 19-digit int64 values that exceed
  // Number.MAX_SAFE_INTEGER. JSON.stringify can't emit them at full precision: a JS number gets
  // truncated, and a string gets quoted (which Go's *int64 rejects with 400). So we stringify
  // with the IDs as strings, then unquote just these numeric fields to emit raw numeric literals
  // that Go unmarshals to int64 at full precision. Only all-digit values are unquoted; a
  // malformed value stays quoted and surfaces as a visible 400 instead of a silent truncation.
  private static readonly INT64_FIELDS: ReadonlyArray<string> = [
    'company_to_bill_id', 'contact_id', 'agency_id',
  ];

  private _stringifyBody(body: PurchaseOrderCreateRequest | PurchaseOrderUpdateRequest): string {
    let json = JSON.stringify(body);
    for (const field of MakersFinancesMgrService.INT64_FIELDS) {
      json = json.replace(new RegExp(`"${field}"\\s*:\\s*"(\\d+)"`, 'g'), `"${field}":$1`);
    }
    return json;
  }
}
